import type { PaymentMethodConfig } from '@/types'

/**
 * PAY-2 (T149-086): the recording-payment method list, shared between
 * PaymentFields and QuickPayFormPage (InvoiceDetailPage no longer needs its
 * own copy after T149-085, it just opens the dialog and lets PaymentFields
 * resolve the methods).
 *
 * Recording a payment already received must never be blocked by unconfigured
 * *tenant-facing* payment methods (bank account number, PromptPay biller id,
 * Stripe keys). When nothing is enabled, 'cash' is always offered so the
 * form stays usable. The caller shows its own always-visible note pointing at
 * Settings for enabling another method.
 */
export type PaymentMethodOptionKey = 'transfer' | 'promptpay' | 'credit_card' | 'cash'

/**
 * The enabled method keys, in the fixed display order used everywhere this
 * list renders (bank transfer, PromptPay, credit card, cash), falling back to
 * `['cash']` when `methods` is absent or nothing in it is enabled.
 */
export function resolvePaymentMethodKeys(
  methods: PaymentMethodConfig | null | undefined,
): PaymentMethodOptionKey[] {
  const keys: PaymentMethodOptionKey[] = []
  if (methods?.bank_transfer?.enabled) keys.push('transfer')
  if (methods?.promptpay?.enabled) keys.push('promptpay')
  if (methods?.credit_card?.enabled) keys.push('credit_card')
  if (methods?.cash?.enabled) keys.push('cash')
  return keys.length > 0 ? keys : ['cash']
}

/**
 * `resolvePaymentMethodKeys` mapped to `{ value, label }` options for a
 * `Select`. `translateLabel` lets each caller resolve the label through its
 * own i18n namespace/key prefix (PaymentFields uses `invoices:payment.methods.*`,
 * QuickPayFormPage uses `payments:methods.*`, both resolve to the same
 * display strings).
 */
export function buildPaymentMethodOptions(
  methods: PaymentMethodConfig | null | undefined,
  translateLabel: (key: PaymentMethodOptionKey) => string,
): { value: PaymentMethodOptionKey; label: string }[] {
  return resolvePaymentMethodKeys(methods).map((key) => ({ value: key, label: translateLabel(key) }))
}
